import React from 'react';
import { Badge } from 'flowbite-react';
import { IIntegrationsSet } from '../interface';
import { getTimezoneOffsetString } from '../../../../lib/utils';
import { useTranslation } from 'react-i18next';

export const getStatusStyles = (status: string | null | undefined, t: any) => {
    const normalizedStatus = status || "finished";
    switch (normalizedStatus) {
        case "pending":
            return {
                border: "border-amber-200",
                bg: "bg-amber-500",
                label: t('configurations.integrations.status.pending'),
                dot: true,
                animation: "animate-pulse"
            };
        case "in_progress":
            return {
                border: "border-blue-200",
                bg: "bg-blue-500",
                label: t('configurations.integrations.status.inProgress'),
                dot: false,
                loading: true,
                animation: "animate-pulse"
            };
        case "failed":
            return {
                border: "border-red-400",
                bg: "bg-red-500",
                label: t('configurations.integrations.status.failed'),
                dot: true,
                animation: "animate-bounce"
            };
        case "not_found":
            return {
                border: "border-red-400",
                bg: "bg-red-500",
                label: t('configurations.integrations.status.notFound'),
                dot: true,
                animation: "animate-bounce"
            };
        case "addAccount":
        case "finished":
        default:
            return {
                border: "border-gray-200",
                bg: "bg-emerald-500",
                label: t('configurations.integrations.status.connected'),
                dot: false
            };
    }
};

export const IntegrationStatusBadge: React.FC<{ status?: string | null, isLoading?: boolean }> = ({ status, isLoading }) => {
    const { t } = useTranslation();

    if (isLoading) {
        return (
            <div className="h-5 w-20 bg-gray-200 dark:bg-neutral-700 animate-pulse rounded-md" />
        );
    }

    // Default to 'not_found' if no status is provided, as per user request
    const displayStatus = status || 'not_found';
    const statusStyle = getStatusStyles(displayStatus, t);

    return (
        <Badge
            color={displayStatus === 'failed' ? 'failure' : displayStatus === 'in_progress' ? 'info' : (displayStatus === 'pending' || displayStatus === 'not_found') ? 'warning' : 'success'}
            size="xs"
            className={statusStyle.animation}
        >
            {statusStyle.label}
        </Badge>
    );
};

export const getSourceHeader = (source: IIntegrationsSet, t: any) => {
    if (source.type === 'google_ads') {
        const selectedAccount = source.accounts?.find(acc => String(acc.id) === String(source.selected_account_id));
        return source?.ads_parent_account_name || selectedAccount?.name || t('configurations.integrations.google_ads');
    }
    if (source.type === 'google_admob') {
        return source.admob_publisher_id || t('configurations.integrations.google_admob');
    }
    if (source.type === 'apple_iap') {
        return source.apple_iap_vendor_number ? `${t('configurations.integrations.vendorNumber')}: ${source.apple_iap_vendor_number}` : t('configurations.integrations.apple_iap');
    }
    if (source.type === 'google_iap') {
        return source.google_iap_bucket_name || t('configurations.integrations.google_iap');
    }
    if (source.type === 'meta_ads') {
        const selectedAccount = source.accounts?.find(acc => String(acc.id) === String(source.selected_account_id));
        return selectedAccount?.name || t('configurations.integrations.meta_ads');
    }
    if (source.type === 'apple_search_ads') {
        const selectedAccount = source.accounts?.find(acc => String(acc.id) === String(source.selected_account_id));
        return source?.ads_sub_account_name || selectedAccount?.name || t('configurations.integrations.apple_search_ads', 'Apple Search Ads');
    }
    return t('configurations.integrations.connected');
};

export const ConnectedSourceDetails: React.FC<{ source: IIntegrationsSet }> = ({ source }) => {
    const { t } = useTranslation();
    if (source.type === 'google_ads') {
        const selectedAccount = source.accounts?.find(acc => String(acc.id) === String(source.selected_account_id));
        return (
            <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400">
                {source.selected_account_id && (
                    <div className="flex items-center gap-1">
                        <span className="font-medium text-gray-600 dark:text-gray-400">{t('configurations.integrations.accountId')}</span>
                        <span className="font-mono">{source.selected_account_id}</span>
                    </div>
                )}
                {selectedAccount && (selectedAccount.currencyCode || selectedAccount.currency_code) && (
                    <div className="flex items-center gap-1">
                        <span className="font-medium text-gray-600 dark:text-gray-400">{t('configurations.integrations.currency')}</span>
                        <span>{selectedAccount.currencyCode || selectedAccount.currency_code}</span>
                    </div>
                )}
                {source.email && (
                    <div className="flex items-center gap-1">
                        <span className="font-medium text-gray-600 dark:text-gray-400">{t('common.email')}:</span>
                        <span className="truncate">{source.email}</span>
                    </div>
                )}
                {selectedAccount && (selectedAccount.reportingTimeZone || selectedAccount.time_zone) && (
                    <div className="flex items-center gap-1">
                        <span className="font-medium text-gray-600 dark:text-gray-400">{t('configurations.integrations.timezone')}</span>
                        <span className="truncate">{selectedAccount.reportingTimeZone || selectedAccount.time_zone}, UTC{getTimezoneOffsetString(selectedAccount.reportingTimeZone || selectedAccount.time_zone || '')}</span>
                    </div>
                )}
            </div>
        );
    }

    if (source.type === 'google_admob') {
        const selectedAccount = source.accounts?.find(acc => {
            const accId = acc.id || (acc as any).publisher_id || (acc as any).publisherId;
            return String(accId) === String(source.admob_publisher_id);
        });
        return (
            <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400">
                {source.admob_publisher_id && (
                    <div className="flex items-center gap-1">
                        <span className="font-medium text-gray-600 dark:text-gray-400">{t('configurations.integrations.publisherId', 'Publisher ID')}</span>
                        <span className="font-mono">{source.admob_publisher_id}</span>
                    </div>
                )}
                {source.email && (
                    <div className="flex items-center gap-1">
                        <span className="font-medium text-gray-600 dark:text-gray-400">{t('common.email')}:</span>
                        <span className="truncate">{source.email}</span>
                    </div>
                )}
                {selectedAccount && (selectedAccount.currencyCode || selectedAccount.currency_code) && (
                    <div className="flex items-center gap-1">
                        <span className="font-medium text-gray-600 dark:text-gray-400">{t('configurations.integrations.currency')}</span>
                        <span>{selectedAccount.currencyCode || selectedAccount.currency_code}</span>
                    </div>
                )}
                {selectedAccount && (selectedAccount.reportingTimeZone || selectedAccount.time_zone) && (
                    <div className="flex items-center gap-1">
                        <span className="font-medium text-gray-600 dark:text-gray-400">{t('configurations.integrations.timezone')}</span>
                        <span className="truncate">{selectedAccount.reportingTimeZone || selectedAccount.time_zone}, UTC{getTimezoneOffsetString(selectedAccount.reportingTimeZone || selectedAccount.time_zone || '')}</span>
                    </div>
                )}
            </div>
        );
    }

    if (source.type === 'apple_iap') {
        return (
            <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400">
                {source.apple_iap_key_id && (
                    <div className="flex items-center gap-1">
                        <span className="font-medium text-gray-600 dark:text-gray-400">{t('configurations.integrations.appleIap.keyIdLabel')}:</span>
                        <span className="font-mono">{source.apple_iap_key_id}</span>
                    </div>
                )}
                {source.apple_iap_issuer_id && (
                    <div className="flex items-center gap-1">
                        <span className="font-medium text-gray-600 dark:text-gray-400">{t('configurations.integrations.appleIap.issuerIdLabel')}:</span>
                        <span className="truncate font-mono">{source.apple_iap_issuer_id}</span>
                    </div>
                )}
            </div>
        );
    }

    if (source.type === 'google_iap') {
        return (
            <div className="mt-1 grid grid-cols-1 gap-y-1 text-[11px] text-gray-500">
                {source.google_iap_service_account_email || source.email && (
                    <div className="flex items-center gap-1">
                        <span className="font-medium text-gray-600 dark:text-gray-400">{t('configurations.integrations.googleIap.serviceAccountEmailLabel')}:</span>
                        <span className="truncate">{source.google_iap_service_account_email || source.email}</span>
                    </div>
                )}

            </div>
        );
    }

    if (source.type === 'meta_ads') {
        const selectedAccount = source.accounts?.find(acc => String(acc.id) === String(source.selected_account_id));
        return (
            <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400">
                {source.selected_account_id && (
                    <div className="flex items-center gap-1">
                        <span className="font-medium text-gray-600 dark:text-gray-400">{t('configurations.integrations.accountId')}</span>
                        <span className="font-mono">{source.selected_account_id}</span>
                    </div>
                )}
                {selectedAccount && (selectedAccount.currencyCode || selectedAccount.currency_code) && (
                    <div className="flex items-center gap-1">
                        <span className="font-medium text-gray-600 dark:text-gray-400">{t('configurations.integrations.currency')}</span>
                        <span>{selectedAccount.currencyCode || selectedAccount.currency_code}</span>
                    </div>
                )}
                {source.email && (
                    <div className="flex items-center gap-1">
                        <span className="font-medium text-gray-600 dark:text-gray-400">{t('common.email')}:</span>
                        <span className="truncate">{source.email}</span>
                    </div>
                )}
                {selectedAccount && (selectedAccount.reportingTimeZone || selectedAccount.time_zone) && (
                    <div className="flex items-center gap-1">
                        <span className="font-medium text-gray-600 dark:text-gray-400">{t('configurations.integrations.timezone')}</span>
                        <span className="truncate">{selectedAccount.reportingTimeZone || selectedAccount.time_zone}, UTC{getTimezoneOffsetString(selectedAccount.reportingTimeZone || selectedAccount.time_zone || '')}</span>
                    </div>
                )}
            </div>
        );
    }

    if (source.type === 'apple_search_ads') {
        const selectedAccount = source.accounts?.find(acc => String(acc.id) === String(source.selected_account_id));
        return (
            <div className="mt-2 flex flex-col gap-2.5 text-[11px] text-gray-500 dark:text-gray-400">
                <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                    {source.selected_account_id && (
                        <div className="flex items-center gap-1.5">
                            <span className="font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide text-[9px]">{t('configurations.integrations.accountId')}</span>
                            <span className="font-mono bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 px-1.5 py-0.5 rounded text-[10px] leading-none border border-indigo-100 dark:border-indigo-800">{source.selected_account_id}</span>
                        </div>
                    )}
                    {selectedAccount && (selectedAccount.currencyCode || selectedAccount.currency_code) && (
                        <div className="flex items-center gap-1.5">
                            <span className="font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide text-[9px]">{t('configurations.integrations.currency')}</span>
                            <span className="bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 font-bold px-2 py-0.5 rounded-full text-[10px] leading-none border border-emerald-100 dark:border-emerald-800">{selectedAccount.currencyCode || selectedAccount.currency_code}</span>
                        </div>
                    )}
                </div>

                {source.apple_ads_client_id && (
                    <div className="flex flex-col gap-1 pr-8">
                        <span className="font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide text-[9px]">{t('configurations.integrations.appleSearchAds.clientIdLabel', 'Client ID')}:</span>
                        <span className="block truncate font-mono text-gray-400 dark:text-gray-500 text-[10px]">{source.apple_ads_client_id}</span>
                    </div>
                )}

                {selectedAccount && (selectedAccount.reportingTimeZone || selectedAccount.time_zone) && (
                    <div className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
                        <span className="font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide text-[9px]">{t('configurations.integrations.timezone')}</span>
                        <span className="truncate">{selectedAccount.reportingTimeZone || selectedAccount.time_zone} (UTC{getTimezoneOffsetString(selectedAccount.reportingTimeZone || selectedAccount.time_zone || '')})</span>
                    </div>
                )}
            </div>
        );
    }


    return null;
};
